// src/components/forms/TaskSubmissionForm.jsx
import React, { useState, useEffect } from 'react';
import { Calendar, Users, AlertCircle, Save, FileText } from 'lucide-react';
import { AssessmentService } from '../../services/taskService';

const TaskSubmissionForm = ({ onSubmit, onCancel, selectedTask = null, initialData = null }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Determine if we're editing
  const isEditing = Boolean(initialData);
  
  const [formData, setFormData] = useState({
    taskId: initialData?.taskId || selectedTask?.id || '',
    date: initialData?.date 
      ? new Date(initialData.date).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0],
    term: initialData?.term || '',
    grade: initialData?.grade || selectedTask?.grade || '',
    subject: initialData?.subject || selectedTask?.subject || '',
    topics: initialData?.topics || selectedTask?.topics || [''],
    expectedSubmissions: initialData?.expectedSubmissions || 0,
    actualSubmissions: initialData?.actualSubmissions || 0,
    absentees: initialData?.absentees || 0
  });

  useEffect(() => {
    loadTasks();
  }, []);

  // Handle initial data and selectedTask changes
  useEffect(() => {
    if (initialData) {
      // Editing mode - prefill with existing submission data
      setFormData({
        taskId: initialData.taskId,
        date: new Date(initialData.date).toISOString().split('T')[0],
        term: initialData.term,
        grade: initialData.grade,
        subject: initialData.subject,
        topics: initialData.topics || [''],
        expectedSubmissions: initialData.expectedSubmissions || 0,
        actualSubmissions: initialData.actualSubmissions || 0,
        absentees: initialData.absentees || 0
      });
    } else if (selectedTask) {
      // New submission with preselected task
      setFormData(prev => ({
        ...prev,
        taskId: selectedTask.id,
        grade: selectedTask.grade,
        subject: selectedTask.subject,
        topics: selectedTask.topics || ['']
      }));
    }
  }, [initialData, selectedTask]);

  const loadTasks = async () => {
    try {
      const taskList = await AssessmentService.getAssessmentTasks();
      setTasks(taskList);
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
  };

  const handleTaskChange = (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (task) {
      setFormData(prev => ({
        ...prev,
        taskId: task.id,
        grade: task.grade,
        subject: task.subject,
        topics: task.topics || ['']
      }));
    }
  };

  const handleTopicChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      topics: prev.topics.map((topic, i) => i === index ? value : topic)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const submissionData = {
        ...formData,
        date: new Date(formData.date),
        topics: formData.topics.filter(t => t.trim() !== '')
      };

      let result;
      if (isEditing) {
        // Update existing submission
        result = await AssessmentService.updateTaskSubmission(initialData.id, submissionData);
        onSubmit({ ...submissionData, id: initialData.id });
      } else {
        // Create new submission
        const submissionId = await AssessmentService.createTaskSubmission(submissionData);
        onSubmit({ ...submissionData, id: submissionId });
      }
    } catch (error) {
      console.error('Error saving task submission:', error);
      setError('Failed to save submission. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-6 text-gray-800 flex items-center gap-2">
        <FileText className="w-6 h-6" />
        {isEditing ? 'Edit Task Submission' : 'Record Task Submission'}
      </h2>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-600" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Task Selection and Date Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Assessment Task *
            </label>
            <select
              value={formData.taskId}
              onChange={(e) => handleTaskChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={isEditing} // Disable task selection when editing
            >
              <option value="">Select a task...</option>
              {tasks.map(task => (
                <option key={task.id} value={task.id}>
                  {task.name} - {task.grade} {task.subject}
                </option>
              ))}
            </select>
            {isEditing && (
              <p className="text-xs text-gray-500 mt-1">Task cannot be changed when editing</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              Submission Date *
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
        </div>

        {/* Term and Academic Info Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Term *
            </label>
            <select
              value={formData.term}
              onChange={(e) => setFormData(prev => ({ ...prev, term: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select term...</option>
              <option value="Term 1">Term 1</option>
              <option value="Term 2">Term 2</option>
              <option value="Term 3">Term 3</option>
              <option value="Term 4">Term 4</option>
              <option value="Semester 1">Semester 1</option>
              <option value="Semester 2">Semester 2</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Grade
            </label>
            <input
              type="text"
              value={formData.grade}
              onChange={(e) => setFormData(prev => ({ ...prev, grade: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Auto-filled from task"
              readOnly
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subject
            </label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Auto-filled from task"
              readOnly
            />
          </div>
        </div>

        {/* Topics Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Topics
          </label>
          <div className="space-y-2">
            {formData.topics.map((topic, index) => (
              <input
                key={index}
                type="text"
                value={topic}
                onChange={(e) => handleTopicChange(index, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Topic from selected task"
                readOnly
              />
            ))}
          </div>
        </div>

        {/* Submission Numbers Row */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5" />
            Submission Statistics
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expected Submissions *
              </label>
              <input
                type="number"
                value={formData.expectedSubmissions}
                onChange={(e) => setFormData(prev => ({ ...prev, expectedSubmissions: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                min="0"
                placeholder="Total class size"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Actual Submissions *
              </label>
              <input
                type="number"
                value={formData.actualSubmissions}
                onChange={(e) => setFormData(prev => ({ ...prev, actualSubmissions: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                min="0"
                placeholder="Number who submitted"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Absentees *
              </label>
              <input
                type="number"
                value={formData.absentees}
                onChange={(e) => setFormData(prev => ({ ...prev, absentees: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                min="0"
                placeholder="Number absent"
              />
            </div>
          </div>
          
          {/* Calculated Summary */}
          <div className="mt-4 p-3 bg-white rounded border">
            <div className="text-sm text-gray-600">
              <p><strong>Submission Rate:</strong> {formData.expectedSubmissions > 0 ? ((formData.actualSubmissions / formData.expectedSubmissions) * 100).toFixed(1) : 0}%</p>
              <p><strong>Non-submissions:</strong> {formData.expectedSubmissions - formData.actualSubmissions - formData.absentees}</p>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-3">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                {isEditing ? 'Updating...' : 'Saving...'}
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {isEditing ? 'Update Submission' : 'Record Submission'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TaskSubmissionForm;